import { useEffect, useMemo, useState, useCallback } from "react";
import {
  fetchBeliefs,
  fetchSchema,
  fetchStories,
} from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import {
  FilterBar,
  SearchInput,
  FilterSelect,
} from "@/components/FilterBar";
import { Tag } from "@/components/Tag";
import { StoryEditor } from "@/components/StoryEditor";
import { Plus, Pencil } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { Link } from "react-router-dom";

export default function StoriesPage() {
  const [stories, setStories] = useState([]);
  const [beliefs, setBeliefs] = useState([]);
  const [schema, setSchema] = useState(null);
  const [query, setQuery] = useState("");
  const [type, setType] = useState("");
  const [freshness, setFreshness] = useState("");
  const [linkedBelief, setLinkedBelief] = useState("");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [s, b, sc] = await Promise.all([
      fetchStories(),
      fetchBeliefs(),
      fetchSchema(),
    ]);
    setStories(s);
    setBeliefs(b);
    setSchema(sc);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const handler = () => load();
    window.addEventListener("indielaw:sync-complete", handler);
    return () => window.removeEventListener("indielaw:sync-complete", handler);
  }, [load]);

  const beliefMap = useMemo(() => {
    const m = {};
    beliefs.forEach((b) => (m[b.id] = b));
    return m;
  }, [beliefs]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return stories.filter((s) => {
      if (q && !s.story_text.toLowerCase().includes(q)) return false;
      if (type && s.story_type !== type) return false;
      if (freshness && s.freshness !== freshness) return false;
      if (linkedBelief && !(s.linked_belief_ids || []).includes(linkedBelief))
        return false;
      return true;
    });
  }, [stories, query, type, freshness, linkedBelief]);

  return (
    <div data-testid="stories-page">
      <PageHeader
        eyebrow="Table 2"
        title="Stories"
        subtitle="Personal, client, news, statistics, metaphors. Each one ladders up to one or more beliefs."
        testId="stories-page-header"
        actions={
          <button
            onClick={() => setCreating(true)}
            data-testid="new-story-button"
            className="inline-flex items-center gap-2 bg-[#141B4D] text-[#F2E9DB] px-5 py-2.5 rounded-sm text-sm font-medium hover:bg-[#141B4D]/90"
          >
            <Plus size={14} strokeWidth={2.5} />
            New story
          </button>
        }
      />

      <FilterBar>
        <SearchInput
          value={query}
          onChange={setQuery}
          placeholder="Search story text…"
          testId="stories-search-input"
        />
        <FilterSelect
          value={type}
          onChange={setType}
          options={schema?.story_types || []}
          placeholder="Any type"
          testId="stories-type-filter"
        />
        <FilterSelect
          value={freshness}
          onChange={setFreshness}
          options={schema?.freshness_values || []}
          placeholder="Any freshness"
          testId="stories-freshness-filter"
        />
        <select
          data-testid="stories-belief-filter"
          value={linkedBelief}
          onChange={(e) => setLinkedBelief(e.target.value)}
          className="bg-white border border-[#141B4D]/20 rounded-sm px-3 py-2 text-sm text-[#141B4D] focus:outline-none focus:ring-2 focus:ring-[#FFD100] min-w-[240px] max-w-[300px]"
        >
          <option value="">Any linked belief</option>
          {beliefs.map((b) => (
            <option key={b.id} value={b.id}>
              {b.belief_text.length > 60
                ? b.belief_text.slice(0, 60) + "…"
                : b.belief_text}
            </option>
          ))}
        </select>
      </FilterBar>

      <div className="mb-3 text-xs text-[#141B4D]/60" data-testid="stories-count">
        {loading ? "Loading…" : `${filtered.length} of ${stories.length} stories`}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b-2 border-[#141B4D]">
              <th className="py-3 pr-4 text-xs uppercase tracking-wider font-bold text-[#141B4D] w-[44%]">
                Story
              </th>
              <th className="py-3 px-3 text-xs uppercase tracking-wider font-bold text-[#141B4D]">
                Type
              </th>
              <th className="py-3 px-3 text-xs uppercase tracking-wider font-bold text-[#141B4D]">
                Linked Beliefs
              </th>
              <th className="py-3 px-3 text-xs uppercase tracking-wider font-bold text-[#141B4D]">
                Freshness
              </th>
              <th className="py-3 px-3 text-xs uppercase tracking-wider font-bold text-[#141B4D]">
                Added
              </th>
              <th className="py-3 pl-3 text-xs uppercase tracking-wider font-bold text-[#141B4D] w-[1%]"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((s) => (
              <tr
                key={s.id}
                data-testid={`story-row-${s.id}`}
                className="border-b border-[#141B4D]/15 hover:bg-[#141B4D]/5 transition-colors"
              >
                <td className="py-4 pr-4 align-top">
                  <div className="text-sm text-[#141B4D] leading-relaxed line-clamp-3">
                    {s.story_text}
                  </div>
                  {s.source && (
                    <div className="text-xs text-[#141B4D]/50 mt-1.5">
                      {s.source}
                    </div>
                  )}
                </td>
                <td className="py-4 px-3 align-top">
                  {s.story_type ? <Tag>{s.story_type}</Tag> : "—"}
                </td>
                <td className="py-4 px-3 align-top">
                  <div className="flex flex-col gap-1.5">
                    {(s.linked_belief_ids || []).slice(0, 3).map((id) => {
                      const b = beliefMap[id];
                      if (!b) return null;
                      return (
                        <Link
                          key={id}
                          to={`/beliefs/${id}`}
                          data-testid={`story-belief-link-${s.id}-${id}`}
                          className="text-xs text-[#141B4D]/80 hover:text-[#141B4D] hover:underline decoration-[#FFD100] underline-offset-2 line-clamp-2 max-w-[260px]"
                        >
                          · {b.belief_text}
                        </Link>
                      );
                    })}
                    {(s.linked_belief_ids || []).length > 3 && (
                      <span className="text-xs text-[#141B4D]/50">
                        +{(s.linked_belief_ids || []).length - 3} more
                      </span>
                    )}
                    {(s.linked_belief_ids || []).length === 0 && (
                      <span className="text-xs text-[#141B4D]/40">None</span>
                    )}
                  </div>
                </td>
                <td className="py-4 px-3 align-top">
                  <Tag
                    variant={
                      s.freshness === "Evergreen"
                        ? "evergreen"
                        : s.freshness === "Time-sensitive"
                          ? "time-sensitive"
                          : "stale"
                    }
                  >
                    {s.freshness}
                  </Tag>
                </td>
                <td className="py-4 px-3 align-top text-xs text-[#141B4D]/60 whitespace-nowrap">
                  {fmtDate(s.date_added)}
                </td>
                <td className="py-4 pl-3 align-top">
                  <button
                    onClick={() => setEditing(s)}
                    data-testid={`story-edit-${s.id}`}
                    className="p-1.5 text-[#141B4D]/60 hover:text-[#141B4D] hover:bg-[#141B4D]/5 rounded-sm"
                    title="Edit"
                  >
                    <Pencil size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="py-12 text-center text-sm text-[#141B4D]/50">
                  No stories match these filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <StoryEditor
        open={creating || !!editing}
        story={editing}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        onSaved={() => load()}
      />
    </div>
  );
}
